import mime from "mime-types";
import { createWorkbook } from "./sheet";

/**
 * This function gets the file extension from a filename.
 *
 * @param {string} filename - The name of the file.
 * @returns {string} The file extension.
 */
export const getFileExtension = (filename) => {
	return /(?:\.([^.]+))?$/.exec(filename)[1];
};

/**
 * Prompts the user to download a file by creating a temporary download link.
 *
 * @param {ArrayBuffer|Array|Blob|string} file - The file data. This can be any object type that is supported by the Blob constructor.
 * @param {string} filename - The name of the file. If the file extension is not provided, it will be inferred from the MIME type.
 * @param {string} [type=""] - The MIME type of the file. If not provided, it will be inferred from the file extension or default to 'application/octet-stream'.
 */
export const promptDownload = (file, filename, type = "") => {
	if (Array.isArray(file)) {
		file = new Uint8Array(file).buffer;
	}

	let fileExtension = getFileExtension(filename);

	if (!fileExtension && type) {
		fileExtension = mime.extension(type);
		filename = `${filename}.${fileExtension}`;
	} else if (fileExtension && !type) {
		type = mime.lookup(fileExtension);
	}

	if (!type) {
		type = file instanceof Blob ? file.type : "application/octet-stream";
	}

	const blob = file instanceof Blob ? file : new Blob([file], { type });

	// Create a temporary download link
	const a = document.createElement("a");
	a.href = URL.createObjectURL(blob);
	a.download = filename;

	// Append the link to the document body and simulate a click to start the download
	document.body.appendChild(a);
	a.click();

	// Remove the temporary link from the document body
	document.body.removeChild(a);
};

/**
 * Exports a table to a file.
 *
 * @param {Array<object>} rows - The rows of the table to be exported.
 * @param {string} filename - The name of the file to which the table will be exported.
 * @param {Object} [options={}] - The options for exporting the table.
 * @param {Array<object>} [options.columns=[]] - The columns of the table to be exported. This should be the "columns" prop for Quasar's QTable. If not provided, all columns will be exported.
 * @param {string} [options.output="csv"] - The format of the output file. Defaults to "csv". For available values, see https://docs.sheetjs.com/docs/api/write-options/
 */
export const exportTable = (rows, filename, { columns = [], output = "csv" } = {}) => {
	let exportRows = rows;

	if (columns.length > 0) {
		exportRows = [];

		// Map each row using its column config
		for (const row of rows) {
			const exportRow = {};

			for (const column of columns) {
				// Only include columns that are in row
				if (Object.hasOwn(row, column.name)) {
					exportRow[column.label] = typeof column.field === "function" ? column.field(row) : row[column.field];
				}
			}

			exportRows.push(exportRow);
		}
	}

	const file = createWorkbook(
		{ Data: exportRows },
		{ type: "array", bookType: output },
	);
	
	promptDownload(file, filename);
};


/**
 * Retrieves only visible rows from the table.
 * 
 * @param {Array<object>} rows - An array of rows.
 * @param {Array<string>} columns - The visible columns
 * @returns {Array<object>} The filtered rows only containing the visible columns.
 */
export const getVisibleRows = (rows, columns) => {
	const visibleRows = [];

	for (const row of rows) {
			const filteredRow = {};

			for (const column of columns) {
					filteredRow[column] = row[column];
			}

			visibleRows.push(filteredRow);
	}

	return visibleRows;
};